import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../api/client";
import useAuth from "../../../hooks/useAuth";
import { PERMISSIONS } from "../../../constants/permissions";
import DispatchPanel from "../components/DispatchPanel";

const today = new Date().toISOString().split("T")[0];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  return (
    <div style={{ background: "#e4ebf5", borderRadius: 999, height: 6, overflow: "hidden", margin: "8px 0 4px" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "#15803d" : "#1d4ed8", borderRadius: 999, transition: "width 0.3s" }} />
    </div>
  );
}

function TaskActionModal({ type, task, onClose, onSuccess }) {
  const [qty, setQty] = useState("");
  const [targetId, setTargetId] = useState("");
  const [earlierStages, setEarlierStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(type !== "ok");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isOk = type === "ok";

  React.useEffect(() => {
    if (isOk) return;
    api.get(`/manufacturing/stages/po/${task.poId}`)
      .then((res) => {
        const all = res.data.data.stages || [];
        const earlier = all.filter((s) => s.stageOrder < task.stageOrder);
        setEarlierStages(earlier);
        if (earlier.length > 0) setTargetId(earlier[0].id);
      })
      .finally(() => setLoadingStages(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const n = Number(qty);
    if (!n || n <= 0) { setError("Enter a valid quantity"); return; }
    if (n > task.partsRemaining) { setError(`Max ${task.partsRemaining}`); return; }
    if (!isOk && !targetId) { setError("Select a target stage"); return; }
    setSubmitting(true);
    setError("");
    try {
      if (isOk) {
        await api.post(`/manufacturing/stages/${task.stageProgressId}/report`, { partsDoneToday: n });
      } else {
        await api.post(`/manufacturing/stages/${task.stageProgressId}/${type}`, { quantity: n, targetStageProgressId: targetId });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const titles = { ok: "Mark as OK", reject: "Reject Parts", rework: "Send for Rework" };
  const submitLabels = { ok: "Submit", reject: "Reject", rework: "Send for Rework" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 28, width: 380, maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0, color: "#0f2d5c", fontSize: 16 }}>{titles[type]}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
        <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 13 }}>
          <strong>{task.poNumber}</strong> — {task.stageName} — {task.partsRemaining} remaining
        </p>
        {loadingStages ? (
          <div style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0" }}>Loading stages…</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 4 }}>Quantity *</label>
            <input
              type="number" min="1" max={task.partsRemaining}
              placeholder={`Max ${task.partsRemaining}`} value={qty}
              onChange={(e) => { setQty(e.target.value); setError(""); }}
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #d6dfed", borderRadius: 8, fontSize: 14, boxSizing: "border-box", marginBottom: 12 }}
              autoFocus required
            />
            {!isOk && (
              <>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 4 }}>Send back to *</label>
                <select
                  value={targetId} onChange={(e) => setTargetId(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #d6dfed", borderRadius: 8, fontSize: 14, boxSizing: "border-box", marginBottom: 8 }}
                  required
                >
                  {earlierStages.length === 0 && <option value="">No earlier stages</option>}
                  {earlierStages.map((s) => <option key={s.id} value={s.id}>{s.stageName}</option>)}
                </select>
              </>
            )}
            {error && <p style={{ margin: "0 0 10px", fontSize: 13, color: "#dc2626" }}>{error}</p>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" onClick={onClose} disabled={submitting} style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 7, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button type="submit" disabled={submitting || (!isOk && earlierStages.length === 0)} style={{ background: "#0f2d5c", color: "#fff", border: "none", borderRadius: 7, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
                {submitting ? "Saving…" : submitLabels[type]}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, onReported }) {
  const [modal, setModal] = useState(null); // 'ok' | 'reject' | 'rework'
  const isActive = task.status === "in_progress" || task.status === "overdue";
  const isOverdue = task.status === "overdue";
  const isDispatch = task.stageType === "dispatch";
  const deadlineOverdue = task.deliveryDate && task.deliveryDate.split("T")[0] < today;

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${isOverdue ? "#fca5a5" : "#e4ebf5"}`,
      borderLeft: `4px solid ${isOverdue ? "#dc2626" : task.status === "pending" ? "#94a3b8" : "#1d4ed8"}`,
      borderRadius: 10,
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div>
          <Link to={`/manufacturing/po/${task.poId}`} style={{ fontWeight: 700, fontSize: 15, color: "#0f2d5c", textDecoration: "none" }}>
            {task.poNumber}
          </Link>
          <div style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>{task.stageName}</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {isOverdue && (
            <span style={{ background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
              Overdue
            </span>
          )}
          {task.status === "pending" && (
            <span style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
              Scheduled
            </span>
          )}
          {task.status === "in_progress" && (
            <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
              In Progress
            </span>
          )}
          {(deadlineOverdue || isOverdue) && task.deliveryDate && (
            <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>
              Due {formatDate(task.deliveryDate)}
            </span>
          )}
          {!deadlineOverdue && !isOverdue && task.deliveryDate && (
            <span style={{ fontSize: 11, color: "#64748b" }}>
              Due {formatDate(task.deliveryDate)}
            </span>
          )}
        </div>
      </div>

      <ProgressBar completed={task.partsCompleted} total={task.partsReceived} />

      <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748b", flexWrap: "wrap" }}>
        <span>Total <strong style={{ color: "#0f2d5c" }}>{task.partsReceived}</strong></span>
        <span>Done <strong style={{ color: "#15803d" }}>{task.partsCompleted}</strong></span>
        <span>Remaining <strong style={{ color: isOverdue ? "#dc2626" : "#0f2d5c" }}>{task.partsRemaining}</strong></span>
      </div>

      {isActive && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {/* On a dispatch stage, progress comes from delivered shipments — not a
              parts-done count — so OK would double-count and is left out. */}
          {!isDispatch && (
            <button onClick={() => setModal("ok")} style={{ flex: 1, padding: "7px 0", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>✓ OK</button>
          )}
          <button onClick={() => setModal("reject")} style={{ flex: 1, padding: "7px 0", background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>✕ Reject</button>
          <button onClick={() => setModal("rework")} style={{ flex: 1, padding: "7px 0", background: "#fff7ed", color: "#c2410c", border: "1px solid #fdba74", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>↺ Rework</button>
        </div>
      )}

      {/* Nothing to ship until parts actually arrive at the stage */}
      {isDispatch && task.partsReceived > 0 && (
        <DispatchPanel
          stageProgressId={task.stageProgressId}
          shipments={task.shipments}
          partsReceived={task.partsReceived}
          onChanged={() => onReported("Shipment updated.")}
        />
      )}

      {modal && (
        <TaskActionModal
          type={modal}
          task={task}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); onReported(); }}
        />
      )}
    </div>
  );
}

function formatDueTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

// A recurring check-in allotted by a manager. Answering here is the same as
// answering the Telegram prompt — whichever comes first closes it out.
function CheckInCard({ item, onAnswered }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // The optional photo on a task that allows one — sent after the answer, so
  // a failed upload never costs the answer itself.
  const [extraPhoto, setExtraPhoto] = useState(null);
  const isNumber = item.responseType === "number";

  const submit = async (answer) => {
    setSubmitting(true);
    setError("");
    let res;
    try {
      res = await api.post(`/manufacturing/allotted-tasks/occurrences/${item.id}/respond`, { answer });
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't record that. Try again.");
      setSubmitting(false);
      return;
    }

    let note = "";
    if (extraPhoto) {
      const form = new FormData();
      form.append("photo", extraPhoto);
      try {
        await api.post(`/manufacturing/allotted-tasks/occurrences/${item.id}/photo?extra=1`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch {
        note = " The photo didn't upload, though.";
      }
    }
    onAnswered(`${res.data.data?.completedTask ? "Done — reminders stopped." : "Check-in recorded!"}${note}`);
  };

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e4ebf5",
      borderLeft: "4px solid #4f46e5",
      borderRadius: 10,
      padding: "14px 16px",
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#0f2d5c" }}>{item.title}</div>
      {item.description && (
        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>{item.description}</div>
      )}
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
        Due {formatDueTime(item.dueAt)} · {item.scheduleLabel}
      </div>
      {item.mode === "until_done" && (
        <div style={{ fontSize: 12, color: "#4f46e5", marginTop: 4, fontWeight: 600 }}>
          Keeps reminding you until you mark it done
        </div>
      )}

      {item.allowPhoto && item.responseType !== "photo" && (
        <label
          style={{
            display: "flex", alignItems: "center", gap: 8, marginTop: 12, minHeight: 40, padding: "0 12px",
            border: "1px dashed #c7d2fe", borderRadius: 7, color: "#3730a3", fontSize: 13, cursor: "pointer",
          }}
        >
          📷 {extraPhoto ? extraPhoto.name : "Add a photo (optional)"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(e) => setExtraPhoto(e.target.files?.[0] || null)}
            style={{ display: "none" }}
          />
        </label>
      )}

      {item.responseType === "photo" ? (
        <PhotoAnswer item={item} submitting={submitting} setSubmitting={setSubmitting} setError={setError} onAnswered={onAnswered} />
      ) : item.responseType === "yes_no" ? (
        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
          <button
            onClick={() => submit(true)} disabled={submitting}
            style={{ flex: 1, padding: "8px 0", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            ✓ Yes, done
          </button>
          <button
            onClick={() => submit(false)} disabled={submitting}
            style={{ flex: 1, padding: "8px 0", background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            ✕ Not done
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); if (text.trim()) submit(text.trim()); }}
          style={{ display: "flex", gap: 6, marginTop: 12 }}
        >
          <input
            value={text}
            onChange={(e) => { setText(e.target.value); setError(""); }}
            placeholder={isNumber ? "Enter a number" : "Type your update"}
            inputMode={isNumber ? "decimal" : undefined}
            aria-label={isNumber ? "Number" : "Your update"}
            style={{ flex: 1, padding: "8px 12px", border: "1px solid #d6dfed", borderRadius: 7, fontSize: 13, boxSizing: "border-box" }}
          />
          <button
            type="submit" disabled={submitting || !text.trim()}
            style={{ padding: "8px 16px", background: "#0f2d5c", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting || !text.trim() ? 0.6 : 1 }}
          >
            Send
          </button>
        </form>
      )}

      {error && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#dc2626" }}>{error}</p>}
    </div>
  );
}

// A photo check-in: take one with the camera (or pick one) and send it.
function PhotoAnswer({ item, submitting, setSubmitting, setError, onAnswered }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");

  const send = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError("");
    const form = new FormData();
    form.append("photo", file);
    if (caption.trim()) form.append("caption", caption.trim());
    try {
      await api.post(`/manufacturing/allotted-tasks/occurrences/${item.id}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onAnswered("Photo sent!");
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't send that photo. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={send} style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
      <label
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 44,
          border: "1px dashed #a5b4fc", borderRadius: 7, background: "#f5f7ff", color: "#3730a3",
          fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}
      >
        📷 {file ? file.name : "Take or choose a photo"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setError(""); }}
          style={{ display: "none" }}
        />
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)"
          aria-label="Caption"
          style={{ flex: 1, padding: "8px 12px", border: "1px solid #d6dfed", borderRadius: 7, fontSize: 13, boxSizing: "border-box" }}
        />
        <button
          type="submit" disabled={submitting || !file}
          style={{ padding: "8px 16px", background: "#0f2d5c", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting || !file ? 0.6 : 1 }}
        >
          {submitting ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
}

// The "no" is already logged; this only collects the explanation that follows it.
function ReasonCard({ item, onAnswered }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/manufacturing/allotted-tasks/occurrences/${item.id}/reason`, { reason: text.trim() });
      onAnswered("Reason recorded.");
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't record that. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderLeft: "4px solid #f59e0b",
      borderRadius: 10,
      padding: "14px 16px",
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#0f2d5c" }}>{item.title}</div>
      <div style={{ fontSize: 13, color: "#b45309", marginTop: 4 }}>
        You marked this not done — what's holding it up?
      </div>
      <form onSubmit={submit} style={{ display: "flex", gap: 6, marginTop: 12 }}>
        <input
          value={text}
          onChange={(e) => { setText(e.target.value); setError(""); }}
          placeholder="Short reason"
          style={{ flex: 1, padding: "8px 12px", border: "1px solid #fcd34d", borderRadius: 7, fontSize: 13, boxSizing: "border-box", background: "#fff" }}
        />
        <button
          type="submit" disabled={submitting || !text.trim()}
          style={{ padding: "8px 16px", background: "#b45309", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting || !text.trim() ? 0.6 : 1 }}
        >
          Send
        </button>
      </form>
      {error && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#dc2626" }}>{error}</p>}
    </div>
  );
}

// A fix raised by someone's "no". Fixed hands it back to the next check-in;
// Can't fix asks why and sends it to the manager.
function FixCard({ fix, onAnswered }) {
  const [blocking, setBlocking] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const post = async (path, body, msg) => {
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/manufacturing/allotted-tasks/fixes/${fix.id}/${path}`, body);
      onAnswered(msg);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't record that. Try again.");
      setSubmitting(false);
    }
  };

  const overdue = new Date(fix.dueAt) < new Date();

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #fed7aa",
      borderLeft: "4px solid #ea580c",
      borderRadius: 10,
      padding: "14px 16px",
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#0f2d5c" }}>
        Fix: {fix.title}
        {fix.attempt > 1 && (
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: "#b91c1c" }}>
            Didn't work · attempt {fix.attempt}
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: "#475569", marginTop: 6, background: "#fef2f2", borderRadius: 7, padding: "7px 10px" }}>
        {fix.reporter?.name} said No: {fix.reason ? `“${fix.reason}”` : "no reason given"}
      </div>
      <div style={{ fontSize: 12, color: overdue ? "#dc2626" : "#64748b", marginTop: 6, fontWeight: overdue ? 600 : 400 }}>
        {overdue ? "Was due" : "Fix by"} {formatDate(fix.dueAt)}
      </div>

      {blocking ? (
        <form
          onSubmit={(e) => { e.preventDefault(); if (reason.trim()) post("blocked", { reason: reason.trim() }, "Sent to your manager."); }}
          style={{ display: "flex", gap: 6, marginTop: 12 }}
        >
          <input
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError(""); }}
            placeholder="What's stopping you?"
            aria-label="What's stopping you?"
            autoFocus
            style={{ flex: 1, padding: "8px 12px", border: "1px solid #fca5a5", borderRadius: 7, fontSize: 13, boxSizing: "border-box" }}
          />
          <button
            type="submit" disabled={submitting || !reason.trim()}
            style={{ padding: "8px 16px", background: "#b91c1c", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting || !reason.trim() ? 0.6 : 1 }}
          >
            Send
          </button>
          <button
            type="button" onClick={() => setBlocking(false)} disabled={submitting}
            style={{ padding: "8px 12px", background: "#fff", color: "#475569", border: "1px solid #d6dfed", borderRadius: 7, fontSize: 13, cursor: "pointer" }}
          >
            Back
          </button>
        </form>
      ) : (
        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
          <button
            onClick={() => post("fixed", {}, "Marked fixed — the next check-in will confirm it.")} disabled={submitting}
            style={{ flex: 1, padding: "8px 0", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            ✓ Fixed
          </button>
          <button
            onClick={() => setBlocking(true)} disabled={submitting}
            style={{ flex: 1, padding: "8px 0", background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            ✕ Can't fix
          </button>
        </div>
      )}
      {error && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#dc2626" }}>{error}</p>}
    </div>
  );
}

function FixSection({ fixes, onAnswered }) {
  if (fixes.length === 0) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#c2410c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
        Fixes <span style={{ fontWeight: 400, color: "#94a3b8" }}>({fixes.length})</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {fixes.map((f) => <FixCard key={f.id} fix={f} onAnswered={onAnswered} />)}
      </div>
    </div>
  );
}

function CheckInSection({ items, reasonItems, onAnswered }) {
  if (items.length === 0 && reasonItems.length === 0) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
        Check-ins <span style={{ fontWeight: 400, color: "#94a3b8" }}>({items.length + reasonItems.length})</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {reasonItems.map((item) => (
          <ReasonCard key={item.id} item={item} onAnswered={onAnswered} />
        ))}
        {items.map((item) => (
          <CheckInCard key={item.id} item={item} onAnswered={onAnswered} />
        ))}
      </div>
    </div>
  );
}

function Section({ title, tasks, onReported, emptyText }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
        {title} <span style={{ fontWeight: 400, color: "#94a3b8" }}>({tasks.length})</span>
      </div>
      {tasks.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 13, padding: "12px 0" }}>{emptyText}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {tasks.map((t) => (
            <TaskCard key={t.stageProgressId} task={t} onReported={onReported} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const [data, setData] = useState({ active: [], scheduled: [] });
  const [checkIns, setCheckIns] = useState([]);
  const [reasonsOwed, setReasonsOwed] = useState([]);
  const [fixes, setFixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [stageRes, checkInRes] = await Promise.allSettled([
        api.get("/manufacturing/stages/my-tasks"),
        api.get("/manufacturing/allotted-tasks/my"),
      ]);
      if (stageRes.status === "fulfilled") {
        setData(stageRes.value.data.data || { active: [], scheduled: [] });
      }
      if (checkInRes.status === "fulfilled") {
        setCheckIns(checkInRes.value.data.data?.pending || []);
        setReasonsOwed(checkInRes.value.data.data?.awaitingReason || []);
        setFixes(checkInRes.value.data.data?.fixes || []);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onReported = useCallback((msg = "Parts logged successfully!") => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
    load(true);
  }, [load]);

  const onCheckInAnswered = useCallback((msg = "Check-in recorded!") => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
    load(true);
  }, [load]);

  // Assigned Tasks used to be its own nav tab. It lives behind this link now,
  // so managers reach the team-wide view from their own task list.
  const canViewAllottedTasks =
    user?.isAdmin ||
    user?.permissions?.includes(PERMISSIONS.MANUFACTURING_ALLOTTED_TASK_VIEW) ||
    user?.permissions?.includes(PERMISSIONS.MANUFACTURING_ALLOTTED_TASK_MANAGE);

  return (
    <div style={{ padding: "20px 16px", maxWidth: 640, margin: "0 auto", minHeight: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c", fontSize: 22 }}>Tasks</h1>
          <p style={{ margin: "0 0 24px", color: "#64748b", fontSize: 14 }}>Your production stages and check-ins.</p>
        </div>
        {canViewAllottedTasks && (
          <Link
            to="/manufacturing/assigned-tasks"
            style={{
              flex: "none", padding: "8px 14px", background: "#eef2ff", color: "#3730a3",
              border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 13, fontWeight: 600,
              textDecoration: "none", whiteSpace: "nowrap",
            }}
          >
            Assigned tasks →
          </Link>
        )}
      </div>

      {loading ? (
        <div style={{ color: "#94a3b8", textAlign: "center", padding: 40 }}>Loading…</div>
      ) : (
        <>
          <FixSection fixes={fixes} onAnswered={onCheckInAnswered} />
          <CheckInSection items={checkIns} reasonItems={reasonsOwed} onAnswered={onCheckInAnswered} />
          <Section
            title="Active"
            tasks={data.active}
            onReported={onReported}
            emptyText="No active stages assigned to you."
          />
          <Section
            title="Scheduled"
            tasks={data.scheduled}
            onReported={onReported}
            emptyText="No upcoming stages scheduled."
          />
        </>
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#0f2d5c", color: "#fff", padding: "10px 20px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          zIndex: 9999, whiteSpace: "nowrap",
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
